import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

import {
  normalizeFrameworkCode,
  regulationLabel,
} from "../../../../../utils/regulationLabel"

/**
 * The framework's display name — the navigator row, the review mask header
 * (`.pnh .t.cur`), the topbar's toasts, and the footer's blocked-inquiry
 * copy all read this SAME resolution rather than each carrying its own
 * fallback.
 *
 * THE SOURCE: `StatementGroup.regulationName` (api/types.ts) — "Server-derived
 * master regulation name for this framework (derive.py's
 * regulation_name_for_framework) — the FE must never re-derive it." v1's own
 * review screens read the identical field the identical way (see
 * `StudioScreenB.tsx`'s `activeRegulation = active?.regulationName ?? null`
 * and `StudioScreenC.tsx`'s `frameworkRegulations` map) — this is that same
 * source, not a second derivation invented for Studio V2.
 *
 * `useStudioV2State` already joins it per framework slug into
 * `regulationNameByFramework` (one statement-group read, no extra request);
 * this function is the ONE fallback rule callers share instead of each
 * re-writing `regulationNameByFramework.get(fw) || fw` inline.
 *
 * A genuine server-provided PROSE name (not token-shaped) is never rewritten
 * — passed through verbatim, exactly as before. It's the two HUMANIZING
 * fallbacks — no server name at all, or a server name that is itself just
 * the seeded master-regulation TOKEN (an identity key, not display copy) —
 * that now prefer `regulationLabel()`'s canonical, translated text over the
 * cosmetic slug humanizer whenever the raw slug/token normalizes to one of
 * `regulationLabel`'s known codes (`normalizeFrameworkCode`, shared with
 * `utils/regulationLabel.ts`'s other callers): "PROP_65" now reads as the
 * catalog's "Kalifornien Prop 65", not the humanizer's bare "Prop 65". Falls
 * back to the humanizer, same as before, when nothing normalizes (an
 * out-of-set slug/token) or when no `translate` was given.
 */
export function frameworkDisplayName(
  framework: string,
  regulationNameByFramework: ReadonlyMap<string, string | null>,
  translate?: (key: TranslationKeys) => string,
): string {
  const server = regulationNameByFramework.get(framework)
  if (!server) {
    const humanized = humanizeFrameworkSlug(framework)
    if (!translate) return humanized
    return regulationLabel(
      normalizeFrameworkCode(framework),
      humanized,
      translate,
    )
  }
  // The server "name" is often the seeded master-regulation TOKEN itself
  // ("REACH_ANNEX_XVII", "PROP_65" — derive.py's REGULATION_NAME_BY_TOKEN),
  // an identity key, not display copy. Token-shaped values (caps, digits,
  // underscores only) are resolved like the slug fallback; anything with
  // lowercase or spaces is real human copy and passes through verbatim, so
  // a genuine server-provided name is never rewritten.
  if (!TOKEN_SHAPE.test(server)) return server
  const humanized = humanizeFrameworkSlug(server)
  if (!translate) return humanized
  return regulationLabel(normalizeFrameworkCode(server), humanized, translate)
}

const TOKEN_SHAPE = /^[A-Z0-9_]+$/

/** Casing for tokens the generic rules below would get wrong — mixed-case
 *  brand spellings ("RoHS") and short lowercase joins ("free"). */
const TOKEN_CASING: Record<string, string> = {
  ROHS: "RoHS",
  CHINA: "China",
  ANNEX: "Annex",
  PROP: "Prop",
  FREE: "free",
}

/** Tokens that are acronyms even when the slug carries them in lowercase
 *  ("reach-svhc", "pfas") — they surface fully capitalized. */
const ACRONYMS = new Set([
  "REACH",
  "SVHC",
  "TSCA",
  "PFAS",
  "POP",
  "POPS",
  "PBT",
  "SCIP",
  "EU",
  "US",
])

/**
 * "REACH_ANNEX_XVII" → "REACH Annex XVII", "PROP_65" → "Prop 65",
 * "CHINA_ROHS_2" → "China RoHS 2", "halogen-free" → "Halogen-free",
 * "TSCA"/"PFAS"/"POPS" unchanged. Underscores become spaces; hyphens are
 * kept (they are part of the vocabulary, e.g. "halogen-free"). A short
 * all-caps token reads as an acronym and stays; anything else is
 * title-cased. Exported for the fallback's own tests.
 */
export function humanizeFrameworkSlug(slug: string): string {
  return slug
    .split("_")
    .map((part) =>
      part
        .split("-")
        .map((token) => {
          const upper = token.toUpperCase()
          if (TOKEN_CASING[upper]) return TOKEN_CASING[upper]
          if (ACRONYMS.has(upper)) return upper
          if (/^\d+$/.test(token)) return token
          // Roman numerals and short unknown acronyms (XVII, IEC) keep
          // their capitals when the slug already carries them; only longer
          // words get title-cased.
          if (token === upper && token.length <= 5) return upper
          return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
        })
        .join("-"),
    )
    .join(" ")
}
